import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useTicketStatuses } from "../../hooks/useTicketStatuses";
import { DefaultTicketStatusKey } from "../../service/CallCenterApi";
import CallCenterNav from "../../components/call-center/CallCenterNav";
const schema = Yup.object({
  statusKey: Yup.string()
    .oneOf(Object.values(DefaultTicketStatusKey))
    .required("Status key is required"),
  name: Yup.string().trim().required("Name is required"),
  colorCode: Yup.string().matches(/^#([0-9a-f]{3}){1,2}$/i, "Invalid Hex color").nullable(),
  isFinalStatus: Yup.boolean(),
  active: Yup.boolean(),
});

const initialValues = {
  statusKey: DefaultTicketStatusKey.OPEN,
  name: "",
  colorCode: "#3B82F6",
  isFinalStatus: false,
  active: true,
};

const CallCenterSettingsPage = () => {
  const statusesApi = useTicketStatuses();
  const [statuses, setStatuses] = useState([]);

  const loadStatuses = async () => {
    try {
      const data = await statusesApi.list();
      setStatuses(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load statuses");
    }
  };

  useEffect(() => {
    loadStatuses();
  }, []);

  const handleSave = async (values, { resetForm }) => {
    try {
      await statusesApi.create({
        statusKey: values.statusKey,
        name: values.name,
        colorCode: values.colorCode || undefined,
        isFinalStatus: Boolean(values.isFinalStatus),
        active: Boolean(values.active),
      });
      toast.success("Status configuration saved");
      resetForm();
      loadStatuses();
    } catch (error) {
      toast.error(error.message || "Failed to save status");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className=" mx-auto">
        <header className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Workflow Settings</h2>
          <p className="text-gray-500">Configure ticket lifecycle and status appearances</p>
          <div className="mt-4">
            <CallCenterNav />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Status Configuration Form */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                <h3 className="font-bold text-gray-700">Add New Status</h3>
                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">Configuration</span>
              </div>

              <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSave}>
                {({ values, errors, touched }) => (
                  <Form className="p-6 space-y-5">
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Base Mapping</label>
                        <Field as="select" name="statusKey" className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 transition-all">
                          {Object.values(DefaultTicketStatusKey).map((key) => (
                            <option key={key} value={key}>{key}</option>
                          ))}
                        </Field>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Display Name</label>
                        <Field name="name" placeholder="e.g. Waiting for Customer" className="w-full p-2.5 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" />
                        {errors.name && touched.name && <div className="text-red-500 text-[10px] font-bold">{errors.name}</div>}
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Color Theme</label>
                        <div className="flex gap-2">
                          <Field name="colorCode" type="color" className="w-12 h-10 border-none rounded cursor-pointer bg-transparent" />
                          <Field name="colorCode" className="flex-1 p-2.5 border border-gray-200 rounded-lg font-mono text-sm uppercase" />
                        </div>
                      </div>

                      {/* Preview Card */}
                      <div className="p-4 bg-gray-900 rounded-xl mt-4">
                        <div className="text-[10px] text-gray-500 font-bold uppercase mb-2">Live UI Preview</div>
                        <div className="flex items-center gap-3 bg-white/5 p-3 rounded-lg border border-white/10">
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-bold border" 
                            style={{ backgroundColor: `${values.colorCode}20`, color: values.colorCode, borderColor: `${values.colorCode}40` }}
                          >
                            {values.name || "Preview Status"}
                          </span>
                          <span className="text-gray-400 text-[10px]">Will appear in Ticket Ledger</span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 pt-2">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <div className="relative flex items-center">
                            <Field type="checkbox" name="isFinalStatus" className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-gray-700">Final Status</span>
                            <span className="text-[10px] text-gray-400 tracking-tight">Closing this ticket stops automation retries.</span>
                          </div>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer group">
                          <Field type="checkbox" name="active" className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                          <span className="text-sm font-bold text-gray-700">Enabled</span>
                        </label>
                      </div>
                    </div>

                    <button type="submit" className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-100 transition-all active:scale-[0.98]">
                      Save Workflow Status
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>

          {/* Status Library List */}
          <div className="lg:col-span-7">
            <div className="flex items-center justify-between mb-4 px-2">
              <h3 className="font-bold text-gray-800 uppercase text-xs tracking-widest">Active Status Library</h3>
              <button onClick={loadStatuses} className="text-[11px] font-bold text-blue-600 flex items-center gap-1 hover:underline">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                Sync Statuses
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {statuses.length === 0 ? (
                <div className="col-span-2 p-12 text-center bg-white rounded-2xl border border-dashed border-gray-300">
                  <div className="text-gray-400 text-sm">No custom statuses configured yet.</div>
                </div>
              ) : (
                statuses.map((status) => (
                  <div 
                    key={`${status.statusKey}-${status.id}`} 
                    className={`bg-white p-4 rounded-xl border-l-4 shadow-sm transition-all hover:shadow-md ${status.active ? 'border-gray-200' : 'opacity-60 bg-gray-50 border-gray-300'}`}
                    style={{ borderLeftColor: status.colorCode || '#E5E7EB' }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{status.statusKey}</span>
                      {status.isFinalStatus && (
                        <span className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-black uppercase">Final</span>
                      )}
                    </div>
                    <div className="text-sm font-bold text-gray-800">{status.name}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <div 
                        className="w-4 h-4 rounded-full border border-black/5" 
                        style={{ backgroundColor: status.colorCode }}
                      />
                      <span className={`text-[10px] font-bold ${status.active ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {status.active ? '● Active' : '○ Inactive'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="mt-8 p-6 bg-blue-600 rounded-2xl text-white relative overflow-hidden">
              <div className="relative z-10">
                <h4 className="font-bold mb-1">Status Pro-Tip</h4>
                <p className="text-xs text-blue-100 leading-relaxed max-w-md">
                  Use high-contrast colors (like Red or Orange) for statuses that require urgent Agent attention, and muted tones for final statuses like "Completed."
                </p>
              </div>
              <svg className="absolute -right-4 -bottom-4 h-32 w-32 text-blue-500 opacity-50" fill="currentColor" viewBox="0 0 20 20"><path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 10-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1a1 1 0 00-2 0v1a1 1 0 002 0zM12 16v-1a1 1 0 00-2 0v1a1 1 0 002 0zM16 16v-1a1 1 0 00-2 0v1a1 1 0 002 0zM15.657 14.243a1 1 0 00-1.414 1.414l.707.707a1 1 0 001.414-1.414l-.707-.707z" /></svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallCenterSettingsPage;